import { PageWithTitle } from 'src/Common/components';
import { useState, useMemo } from 'react';
import styled from '@emotion/styled';
import {
  FiltersMenu,
  ActiveFilterList
} from '../components/ArbitrageFilters';
import { Badge, Box, IconButton, Stack } from '@mui/material';
import { ProfitDrawerProvider, useProfitDrawer } from '../context';
import { LiveBadge } from 'src/Common/components/LiveBadge/LiveBadge';
import { FindCrypto } from '../components/FindCrypto';
import {
  ArbitrageOrderBooksProvider,
  useArbitrageOrderBooks
} from '../context/ArbitrageOrderBooksProvider/ArbitrageOrderBooksProvider';
import { ArbitrageProfitTable } from '../components/ArbitrageProfitTable';
import { ResubscribeOrderbook } from '../components/ResubscribeOrderbook';
import { DrawerArbitrageProfit } from '../components/DrawerArbitrageProfit';
import { ReloadPairs } from '../components/ReloadPairs';
import { ToggleUpdatesButton } from 'src/Common/components/ToggleUpdatesButton/ToggleUpdatesButton';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import { NotificationsDrawer } from '../components/NotificationsDrawer';
import { useProfitAlerts } from '../hooks/useProfitAlerts';

export interface ArbitrageTab {
  label: string;
}

const ArbitrageProfitPageContent = () => {
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const { subscribeToCrypto, currentCryptoId } = useProfitDrawer();

  const { pricesResponse, isConnected, notifications } =
    useArbitrageOrderBooks();

  // Get the most recent notification for sound alerts
  const latestNotification = useMemo(
    () => notifications?.[notifications.length - 1],
    [notifications],
  );
  useProfitAlerts(latestNotification);

  return (
    <>
      <PageWithTitle>
        <Stack direction="row">
          <Box style={{ width: '100%' }}>
            <Toolbar>
              <ActiveFilterList />
              <Box display="flex" justifyContent="space-between">
                <Stack direction="row" spacing={2} alignItems="center">
                  <FiltersMenu />
                  <FindCrypto
                    onFindCrypto={(crypto) => subscribeToCrypto(String(crypto.id), crypto.symbol)}
                    sx={{ minWidth: 250 }}
                  />
                </Stack>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <LiveBadge isLive={isConnected} />
                  <div style={{ marginLeft: '.5rem' }}>
                    <ToggleUpdatesButton subsystem="orderbooks" />
                  </div>
                  <div style={{ marginLeft: '.5rem' }}>
                    <ResubscribeOrderbook />
                  </div>
                  <div style={{ marginLeft: '.5rem' }}>
                    <ReloadPairs />
                  </div>
                  <div style={{ marginLeft: '.5rem' }}>
                    <IconButton
                      aria-label="notifications"
                      onClick={() => setNotificationsOpen((prev) => !prev)}
                    >
                      <Badge
                        badgeContent={notifications?.length ?? 0}
                        color="secondary"
                      >
                        <NotificationsOutlinedIcon />
                      </Badge>
                    </IconButton>
                  </div>
                </div>
              </Box>
            </Toolbar>

            <ArbitrageProfitTable
              setActiveCryptoDetail={subscribeToCrypto}
              isLoading={isConnected && !pricesResponse?.length}
              data={isConnected ? pricesResponse ?? [] : []}
            />
            <DrawerArbitrageProfit
              open={Boolean(currentCryptoId)}
              onClose={() => subscribeToCrypto(null)}
            />
          </Box>
          <NotificationsDrawer
            isOpen={notificationsOpen}
            notifications={notifications ?? []}
            onClose={() => setNotificationsOpen(false)}
          />
        </Stack>
      </PageWithTitle>
    </>
  );
};

export const ArbitrageProfitPage = () => {
  return (
    <ArbitrageOrderBooksProvider>
      <ProfitDrawerProvider>
        <ArbitrageProfitPageContent />;
      </ProfitDrawerProvider>
    </ArbitrageOrderBooksProvider>
  );
};

const Toolbar = styled.div({
  display: 'flex',
  flexDirection: 'column',
  width: '100%'
});
